import { useCallback, useState, type MouseEvent } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import { Button, Grid, Menu, MenuItem, Stack, Typography } from '@mui/material';
import type { Area } from 'react-easy-crop';
import { ImageCropperDialog } from '@/components/shared/image-cropper/dialog';
import { ImageUploader } from '@/components/shared/image-uploader';
import {
  UserAvatar,
  type UserAvatarProps,
} from '@/components/shared/user-avatar';
import { useNotification } from '@/context/notification';
import {
  useDeleteProfileImage,
  useUpdateProfileImage,
} from '@/data/account/profile';
import { lineClamp } from '@/utils/line-clamp';

export type UserAvatarEditProps = UserAvatarProps & {
  email: string;
};

export const UserAvatarEdit = ({
  imageSource,
  name,
  email,
}: UserAvatarEditProps) => {
  const [rawUpload, setRawUpload] = useState<string>('');
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [imageLoading, setImageLoading] = useState(false);
  const { mutate: updateProfileImage } = useUpdateProfileImage();
  const { mutate: deleteProfileImage } = useDeleteProfileImage();
  const { toast } = useNotification();

  const menuOpen = Boolean(menuAnchorEl);

  const handleMenuOpen = useCallback((event: MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleRemoveAvatar = () => {
    deleteProfileImage();
    handleMenuClose();
  };

  const handleUpload = (image: string | null) => {
    handleMenuClose();

    if (image) {
      setRawUpload(image);
    }
  };

  const handleCrop = ({
    cropArea,
    originalImage,
  }: {
    cropArea: Area;
    originalImage: string;
  }) => {
    setImageLoading(true);
    updateProfileImage(
      { cropArea, image: originalImage },
      {
        onSuccess: () => setImageLoading(false),
        onError: () => {
          toast({
            content: `Erro ao atualizar imagem de perfil`,
            type: 'error',
          });
          setImageLoading(false);
        },
      }
    );

    setRawUpload('');
  };

  const handleDialogClose = () => {
    setRawUpload('');
  };

  return (
    <>
      <Grid
        alignItems="center"
        spacing={2}
        container
        sx={{ flexWrap: 'nowrap' }}
      >
        <UserAvatar
          imageSource={imageSource}
          name={name}
          size={96}
          loading={imageLoading}
        />
        <Stack sx={{ overflow: 'hidden' }}>
          <Typography variant="body1" fontWeight="bold" sx={lineClamp(1)}>
            {name}
          </Typography>
          <Typography
            variant="body2"
            mb={1}
            sx={{ ...lineClamp(1), wordWrap: 'break-word' }}
          >
            {email}
          </Typography>
          <Button
            endIcon={<EditIcon />}
            sx={{
              padding: 0,
              justifyContent: 'flex-start',
              width: 'fit-content',
            }}
            onClick={handleMenuOpen}
          >
            Alterar imagem
          </Button>
          <Menu
            anchorEl={menuAnchorEl}
            elevation={1}
            open={menuOpen}
            onClose={handleMenuClose}
          >
            <ImageUploader onUpload={handleUpload}>
              <MenuItem sx={{ flex: 1 }}>Alterar imagem</MenuItem>
            </ImageUploader>
            <MenuItem onClick={handleRemoveAvatar}>Remover imagem</MenuItem>
          </Menu>
        </Stack>
      </Grid>
      {!!rawUpload && (
        <ImageCropperDialog
          image={rawUpload}
          onClose={handleDialogClose}
          onCrop={handleCrop}
        />
      )}
    </>
  );
};
